import React, { useEffect, useMemo, useState } from "react";
import "../styles/shared-ui.css";
import "../styles/job-workload.css";
import { useAppContext } from "../context/AppContext";
import { getJobInformation, getJobWorkload } from "../services/jobService";
import SearchableDropdown from "../components/ui/SearchableDropdown";
import { toast } from "../utils/toast";

const RACI_LABELS = {
  R: "Responsible",
  A: "Accountable",
  C: "Consulted",
  I: "Informed",
};
const RACI_COLORS = {
  R: { bg: "var(--blue-tint-100)", color: "var(--blue-ink-700)" },
  A: { bg: "var(--amber-tint-100)", color: "var(--amber-ink-700)" },
  C: { bg: "var(--violet-tint-100)", color: "var(--violet-ink-700)" },
  I: { bg: "var(--slate-100)", color: "var(--slate-600)" },
};

const describeRecurrence = (occurrences, frequencyInterval, frequencyPeriod) => {
  const occ = Number(occurrences);
  const interval = Number(frequencyInterval);
  if (
    !Number.isFinite(occ) ||
    !Number.isFinite(interval) ||
    occ <= 0 ||
    interval <= 0
  ) {
    return "-";
  }
  const periodLabel = String(frequencyPeriod || "WEEK").toLowerCase();
  const periodText = interval === 1 ? periodLabel : `${periodLabel}s`;
  const timesText = occ === 1 ? "time" : "times";
  return `${occ} ${timesText} in ${interval} ${periodText}`;
};

const bandColor = (band) => {
  const key = String(band || "").toLowerCase();
  if (key.includes("over")) return "#dc2626";
  if (key.includes("under")) return "#2563eb";
  if (key.includes("optimal") || key.includes("balanced")) return "#16a34a";
  return "#475569";
};

const buildSearchLabel = (job) => {
  const name = String(job?.job_name || job?.jobName || job?.name || "").trim();
  const code = String(job?.job_code || job?.jobCode || "").trim();
  const company = String(
    job?.company_name || job?.companyName || job?.company?.name || "",
  ).trim();

  const jobPart = name ? `${name}${code ? ` (${code})` : ""}` : code || "-";
  return `${jobPart} | ${company || "-"}`;
};

const JobWorkloadPage = () => {
  const { setActiveSection } = useAppContext();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLabel, setSelectedLabel] = useState("");
  const [selectedJob, setSelectedJob] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        const result = await getJobInformation();
        if (!cancelled) setJobs(Array.isArray(result) ? result : []);
      } catch (e) {
        toast.error(e?.message || "Failed to load job workload data");
        if (!cancelled) setJobs([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const jobOptions = useMemo(() => {
    return jobs.map((job) => {
      const label = buildSearchLabel(job);
      return {
        label,
        value: String(job.jobId ?? job.job_id ?? job.id),
        job,
      };
    });
  }, [jobs]);

  const handleDropdownChange = (label, option) => {
    setSelectedLabel(label);
    if (option) {
      setSelectedJob(option.job);
    } else {
      const found = jobOptions.find(
        (o) => o.label.toLowerCase() === label.trim().toLowerCase(),
      );
      if (found) {
        setSelectedJob(found.job);
      } else {
        setSelectedJob(null);
      }
    }
  };

  const handleAnalyzeWorkload = async () => {
    if (!selectedJob) return;

    const jobId = selectedJob.jobId ?? selectedJob.job_id ?? selectedJob.id;
    if (!jobId) {
      toast.error("Invalid job selection");
      return;
    }

    try {
      setAnalyzing(true);
      setAnalysisData(null);
      const data = await getJobWorkload(jobId);
      if (data) {
        setAnalysisData(data);
        toast.success(
          `Analysis loaded for: ${data.job_name || data.name || selectedJob.jobName}`,
        );
      } else {
        toast.error("Failed to load job details");
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Failed to analyze workload");
    } finally {
      setAnalyzing(false);
    }
  };

  const metrics = useMemo(() => {
    if (!analysisData) return null;

    const taskList = analysisData.tasks || analysisData.jobTasks || [];
    const tasks = Array.isArray(taskList)
      ? taskList
          .map((tj) => {
            const task = tj?.task || tj;
            if (!task) return null;
            const raciRole = tj?.role || task.role || "R";
            const allocationPct = Number(tj.time_allocation_percentage ?? 0);
            // Backend sends process/rework/duration in minutes; display in hours
            const processingTime = Number(task.expected_process_time ?? 0) / 60;
            const reworkTime = Number(task.expected_rework_time ?? 0) / 60;
            const taskTimePerOccurrence =
              task.task_duration != null
                ? Number(task.task_duration) / 60
                : processingTime + reworkTime;
            const weeklyHrs = Number(
              tj.weekly_workload_hrs ?? tj.smoothed_weekly_hrs ?? 0,
            );
            const occurrenceSummary = describeRecurrence(
              task.occurrences,
              task.frequency_interval,
              task.frequency_period,
            );
            return {
              ...task,
              raciRole,
              allocationPct,
              processingTime,
              reworkTime,
              taskTimePerOccurrence,
              weeklyHrs,
              occurrenceSummary,
            };
          })
          .filter(Boolean)
      : [];

    const aggregated = analysisData.aggregated || {};
    const totalWeeklyHrs = Number(aggregated.total_weekly_workload_hrs ?? 0);
    const rawCapacityHrs = Number(aggregated.raw_capacity_hrs ?? 0);
    const effectiveCapacityHrs = Number(aggregated.effective_capacity_hrs ?? 0);
    const utilisationPct = Number(aggregated.utilisation_pct ?? 0);
    const utilisationBand =
      aggregated.status || aggregated.utilisation_band || "-";
    const isOverloaded = utilisationPct > 100;

    const daysPerWeek = Number(analysisData.days_per_week ?? 0);
    const hoursPerDay = Number(analysisData.hours_per_day ?? 0);
    const capacityBuffer = Number(analysisData.capacity_buffer ?? 0);

    return {
      tasks,
      totalWeeklyHrs,
      rawCapacityHrs,
      effectiveCapacityHrs,
      utilisationPct,
      utilisationBand,
      isOverloaded,
      daysPerWeek,
      hoursPerDay,
      capacityBuffer,
    };
  }, [analysisData]);

  const goToTask = (taskId, section) => {
    try {
      localStorage.setItem("activeTaskId", String(taskId));
      localStorage.setItem("taskReturnTo", "job-workload");
    } catch {}
    setActiveSection(section, { taskId });
  };

  return (
    <div className="page-container job-workload-page">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Job Workload</h2>
            <p className="page-subtitle">
              Search jobs and inspect the assigned task workload.
            </p>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="role-card workload-search-card">
          <div className="workload-search-header">
            <div>
              <div className="section-title" style={{ marginBottom: 6 }}>
                Select Job
              </div>
              <div className="workload-search-help">
                Search and select a job by Name, Code, or Company.
              </div>
            </div>
            <div className="workload-search-count">
              {loading
                ? "Loading..."
                : `${jobOptions.length} job${jobOptions.length === 1 ? "" : "s"}`}
            </div>
          </div>

          <div className="workload-search-row">
            <div className="workload-dropdown-wrap">
              <SearchableDropdown
                id="workload-job-dropdown"
                value={selectedLabel}
                options={jobOptions}
                placeholder="Search job by name, code, or company..."
                clearOnFocus
                onChange={handleDropdownChange}
                disabled={loading || analyzing}
              />
            </div>
            <button
              className="primary-btn"
              disabled={!selectedJob || analyzing}
              onClick={handleAnalyzeWorkload}
              style={{ whiteSpace: "nowrap", minWidth: "fit-content" }}
            >
              {analyzing ? "Analyzing..." : "Analyze Workload"}
            </button>
          </div>
        </div>

        {analyzing && (
          <div className="role-card workload-analysis-card loading-state">
            <div className="no-results">
              Analyzing workload data. Please wait...
            </div>
          </div>
        )}

        {!analyzing && analysisData && metrics && (
          <div className="workload-analysis-container fade-in">
            <div className="role-card workload-analysis-card">
              <div className="section-title" style={{ marginBottom: 16 }}>
                Workload Analysis: {analysisData.job_name || analysisData.name}{" "}
                {analysisData.job_code || analysisData.jobCode
                  ? `(${analysisData.job_code || analysisData.jobCode})`
                  : ""}
              </div>

              {metrics.isOverloaded && (
                <div
                  className="no-results"
                  style={{
                    marginBottom: 12,
                    color: "var(--red-ink-600)",
                    fontWeight: 700,
                    textAlign: "left",
                  }}
                >
                  Overload: Workload exceeds 100% of weekly capacity.
                </div>
              )}

              <div className="analysis-grid">
                <div className="analysis-stat-card">
                  <div className="stat-label">Weekly Workload</div>
                  <div className="stat-value">
                    {metrics.totalWeeklyHrs.toFixed(2)} hrs/week
                  </div>
                  <div className="stat-sub">Total attributed weekly hours</div>
                </div>

                <div className="analysis-stat-card">
                  <div className="stat-label">Raw Capacity</div>
                  <div className="stat-value">
                    {metrics.rawCapacityHrs.toFixed(2)} hrs/week
                  </div>
                  <div className="stat-sub">
                    {metrics.daysPerWeek} days/week × {metrics.hoursPerDay}{" "}
                    hrs/day
                  </div>
                </div>

                <div className="analysis-stat-card">
                  <div className="stat-label">Effective Capacity</div>
                  <div className="stat-value">
                    {metrics.effectiveCapacityHrs.toFixed(2)} hrs/week
                  </div>
                  <div className="stat-sub">
                    After {metrics.capacityBuffer}% capacity buffer
                  </div>
                </div>

                <div className="analysis-stat-card">
                  <div className="stat-label">Utilisation Rate</div>
                  <div
                    className="stat-value"
                    style={{ color: bandColor(metrics.utilisationBand) }}
                  >
                    {metrics.utilisationPct}%
                  </div>
                  <div
                    className="stat-sub"
                    style={{
                      color: bandColor(metrics.utilisationBand),
                      fontWeight: 600,
                    }}
                  >
                    {metrics.utilisationBand}
                  </div>
                </div>

                <div className="analysis-stat-card">
                  <div className="stat-label">Total Tasks</div>
                  <div className="stat-value">{metrics.tasks.length}</div>
                  <div className="stat-sub">Linked process tasks</div>
                </div>
              </div>

              <div
                className="utilization-meter-container"
                style={{ marginTop: 24 }}
              >
                <div className="utilization-meter-label">
                  <span>Capacity Utilisation</span>
                  <span
                    style={{
                      fontWeight: 600,
                      color: bandColor(metrics.utilisationBand),
                    }}
                  >
                    {metrics.utilisationPct}% · {metrics.utilisationBand}
                  </span>
                </div>
                <div className="utilization-meter-bg">
                  <div
                    className={`utilization-meter-bar ${metrics.isOverloaded ? "overloaded" : ""}`}
                    style={{
                      width: `${Math.min(metrics.utilisationPct, 100)}%`,
                    }}
                  />
                </div>
              </div>
            </div>

            <div
              className="role-card workload-analysis-card"
              style={{ marginTop: 16 }}
            >
              <div className="section-title" style={{ marginBottom: 12 }}>
                Assigned Tasks Detail
              </div>
              <div className="workload-table-wrap">
                <div className="roles-table workload-tasks-table">
                  <div className="roles-table-header">
                    <div className="cell">Task Code</div>
                    <div className="cell">Task Name</div>
                    <div className="cell">RACI Role</div>
                    <div className="cell">Occurrence</div>
                    <div className="cell">Task Time per Occurrence</div>
                    <div className="cell">Weekly Hrs</div>
                    <div className="cell actions">Actions</div>
                  </div>

                  {metrics.tasks.length === 0 ? (
                    <div className="no-results">
                      No tasks are currently linked to this job.
                    </div>
                  ) : (
                    metrics.tasks.map((task) => {
                      const taskId = task.task_id || task.id;
                      const taskName = task.task_name || task.name || "-";
                      const taskCode = task.task_code || task.code || "-";
                      const role = task.raciRole;
                      const roleColors = RACI_COLORS[role] || RACI_COLORS["I"];

                      return (
                        <div key={String(taskId)} className="roles-table-row">
                          <div className="cell">{taskCode}</div>
                          <div className="cell">{taskName}</div>
                          <div className="cell">
                            <div className="raci-cell-content">
                              <span
                                className="raci-badge"
                                style={{
                                  background: roleColors.bg,
                                  color: roleColors.color,
                                }}
                                title={RACI_LABELS[role] || role}
                              >
                                {role}
                              </span>
                              <div className="raci-cell-text">
                                <span className="raci-label">
                                  {RACI_LABELS[role] || role}
                                </span>
                                <span className="raci-allocation">
                                  {task.allocationPct.toFixed(2)}% allocation
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="cell">{task.occurrenceSummary}</div>
                          <div className="cell">
                            <div className="task-time-cell">
                              <span className="task-time-total">
                                {task.taskTimePerOccurrence.toFixed(2)} hrs
                              </span>
                              <span className="task-time-breakdown">
                                {task.processingTime.toFixed(2)} proc +{" "}
                                {task.reworkTime.toFixed(2)} rework
                              </span>
                            </div>
                          </div>
                          <div className="cell">
                            <span className="effective-hrs">
                              {task.weeklyHrs.toFixed(2)} hrs
                            </span>
                          </div>
                          <div
                            className="cell actions"
                            style={{ display: "flex", gap: 6 }}
                          >
                            <button
                              className="secondary-btn sm"
                              onClick={() => goToTask(taskId, "task-detail")}
                            >
                              View
                            </button>
                            <button
                              className="secondary-btn sm"
                              onClick={() => goToTask(taskId, "task-edit")}
                            >
                              Edit
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobWorkloadPage;
